/**
 * Mobile Context
 * Provides mobile detection and utilities for mobile-optimized components
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

const MobileContext = createContext();

const readDeviceFlags = () => {
  if (typeof window === 'undefined') {
    return { isMobile: false, isTablet: false, hasTouch: false, orientation: 'portrait' };
  }
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    isMobile: width <= 768 && height <= 1024,
    isTablet: width > 768 && width <= 1024,
    hasTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    orientation: width > height ? 'landscape' : 'portrait',
  };
};

export const useMobile = () => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
};

export const MobileProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(() => readDeviceFlags().isMobile);
  const [isTablet, setIsTablet] = useState(() => readDeviceFlags().isTablet);
  const [hasTouch, setHasTouch] = useState(() => readDeviceFlags().hasTouch);
  const [orientation, setOrientation] = useState(() => readDeviceFlags().orientation);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Mobile detection (phones only, not tablets)
      const mobile = width <= 768 && height <= 1024;
      const tablet = width > 768 && width <= 1024;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setHasTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
      setOrientation(width > height ? 'landscape' : 'portrait');
    };

    // Initial check
    checkDevice();

    // Listen for resize events
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  const value = {
    isMobile,
    isTablet,
    hasTouch,
    orientation,
    isLandscape: orientation === 'landscape',
    isPortrait: orientation === 'portrait',
    touchSupported: hasTouch,
    // Utility functions
    getScreenSize: () => ({ width: window.innerWidth, height: window.innerHeight }),
    isSmallScreen: () => window.innerWidth <= 480,
    isMediumScreen: () => window.innerWidth > 480 && window.innerWidth <= 768,
    isLargeScreen: () => window.innerWidth > 768,
    // Touch feedback variants for Framer Motion
    getTouchFeedbackVariants: () => ({
      tap: { scale: 0.95 },
      hover: { scale: 1.05 },
    }),
    // Detect swipe gestures
    detectSwipe: (element, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown) => {
      if (!element || !hasTouch) return () => {};
      
      let touchStartX = null;
      let touchStartY = null;
      const minSwipeDistance = 50;

      const handleTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchEnd = (e) => {
        if (touchStartX === null || touchStartY === null) return;

        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const deltaX = touchStartX - touchEndX;
        const deltaY = touchStartY - touchEndY;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Horizontal swipe
          if (Math.abs(deltaX) > minSwipeDistance) {
            if (deltaX > 0 && onSwipeLeft) {
              onSwipeLeft();
            } else if (deltaX < 0 && onSwipeRight) {
              onSwipeRight();
            }
          }
        } else {
          // Vertical swipe
          if (Math.abs(deltaY) > minSwipeDistance) {
            if (deltaY > 0 && onSwipeUp) {
              onSwipeUp();
            } else if (deltaY < 0 && onSwipeDown) {
              onSwipeDown();
            }
          }
        }

        touchStartX = null;
        touchStartY = null;
      };

      element.addEventListener('touchstart', handleTouchStart);
      element.addEventListener('touchend', handleTouchEnd);

      return () => {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchend', handleTouchEnd);
      };
    },
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
};

